import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { SocialShareButtons } from '@/components/public/SocialShareButtons'
import LockerInvite from './LockerInvite'
import type {
  LockerRoomPublicConfig,
  LockerRoomQuote,
  LockerRoomWishlistItem,
  LockerRoomTopMomentsConfig,
  LockerRoomThemeConfig,
} from '@/types/locker-rooms'

const CORE_API = import.meta.env.VITE_CORE_API_URL || ''

// ─── Locker room data from public endpoint ──────────────────────────────

interface LockerRoomData {
  theme?: LockerRoomThemeConfig
  hero?: { headline?: string; subheadline?: string }
  public_name?: string
  blurb?: string
  donation_share_code?: string
  bio?: string
  public_slug?: string
  featured_athletes?: Array<{ first_name?: string }>
  roster_count?: number
  // New showcase fields (from public_config via materialized view)
  displayName?: string
  about?: string
  quote?: LockerRoomQuote
  goals?: string[]
  wishlist?: LockerRoomWishlistItem[]
  topMoments?: LockerRoomTopMomentsConfig
  visibility?: { isPublic?: boolean; showShareLink?: boolean }
}

type LoadStatus = 'loading' | 'error' | 'ready' | 'invite'

// ─── Theme system ───────────────────────────────────────────────────────

interface ThemeStyle {
  bg: string
  cardBg: string
  headerBg: string
  text: string
  mutedText: string
  badge: string
  button: string
  isBold?: boolean
}

const THEMES: Record<string, ThemeStyle> = {
  electric: {
    bg: 'from-amber-100 via-fuchsia-100 to-indigo-100',
    cardBg: 'bg-white/85 backdrop-blur',
    headerBg: 'from-indigo-600 to-fuchsia-600',
    text: 'text-indigo-950',
    mutedText: 'text-indigo-700/70',
    badge: 'bg-indigo-900 text-amber-100',
    button: 'bg-indigo-900 text-white hover:bg-indigo-800',
  },
  earth: {
    bg: 'from-emerald-50 via-sand to-amber-50',
    cardBg: 'bg-white/85 backdrop-blur',
    headerBg: 'from-emerald-700 to-emerald-800',
    text: 'text-emerald-950',
    mutedText: 'text-emerald-700/70',
    badge: 'bg-emerald-900 text-amber-50',
    button: 'bg-emerald-900 text-amber-50 hover:bg-emerald-800',
  },
  midnight: {
    bg: 'from-slate-900 via-indigo-900 to-slate-800',
    cardBg: 'bg-white/10 backdrop-blur',
    headerBg: 'from-amber-500 to-amber-600',
    text: 'text-white',
    mutedText: 'text-gray-400',
    badge: 'bg-amber-400 text-slate-900',
    button: 'bg-amber-400 text-slate-900 hover:bg-amber-300',
    isBold: true,
  },
  clean: {
    bg: 'from-slate-50 to-blue-50/30',
    cardBg: 'bg-white',
    headerBg: 'from-blue-600 to-blue-700',
    text: 'text-slate-800',
    mutedText: 'text-slate-500',
    badge: 'bg-blue-600 text-white',
    button: 'bg-blue-600 text-white hover:bg-blue-700',
  },
}

const DEFAULT_THEME = THEMES.electric

// ─── Data hook ──────────────────────────────────────────────────────────

function useLockerRoom(slug: string | undefined) {
  const [state, setState] = useState<{ status: LoadStatus; data: LockerRoomData | null; error: string }>({
    status: 'loading',
    data: null,
    error: '',
  })

  useEffect(() => {
    let cancelled = false
    if (!slug) {
      setState({ status: 'error', data: null, error: 'Locker room not found.' })
      return () => {}
    }

    const fetchLocker = async () => {
      setState({ status: 'loading', data: null, error: '' })
      try {
        const base = CORE_API || (typeof window !== 'undefined' ? window.location.origin : 'https://projecttower.org')
        const url = `${base}/api/v1/locker-room/public?slug=${encodeURIComponent(slug)}`

        const response = await fetch(url)
        const payload = await response.json().catch(() => ({}))

        if (response.status === 404) {
          if (!cancelled) setState({ status: 'invite', data: null, error: '' })
          return
        }
        if (!response.ok) {
          const errMsg = typeof payload?.error === 'string' ? payload.error : payload?.error?.message || 'Unable to load'
          throw new Error(errMsg)
        }

        if (!cancelled) {
          const lr = payload?.data?.locker_room || payload?.locker_room || null
          setState({ status: 'ready', data: lr, error: '' })
        }
      } catch (error: unknown) {
        if (!cancelled) setState({ status: 'error', data: null, error: (error as Error)?.message || 'Unavailable' })
      }
    }

    fetchLocker()
    return () => { cancelled = true }
  }, [slug])

  return state
}

// ─── Component ──────────────────────────────────────────────────────────

export default function LockerPublic() {
  const { slug } = useParams()
  const { status, data, error } = useLockerRoom(slug)

  const theme = useMemo((): ThemeStyle => {
    const styleKey = (data?.theme?.style || '').toLowerCase()
    return THEMES[styleKey] || DEFAULT_THEME
  }, [data?.theme?.style])

  const accent = data?.theme?.accentColor || (theme === THEMES.midnight ? '#f59e0b' : '#4f46e5')

  const shareUrl = useMemo(() => {
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://projecttower.org'
    return `${base}/locker/${slug}`
  }, [slug])

  if (status === 'invite') return <LockerInvite code={slug} />

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50/30">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
      </div>
    )
  }

  if (status === 'error' || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50/30 px-4">
        <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow-lg">
          <h1 className="text-xl font-bold text-slate-800">Locker Room Not Found</h1>
          <p className="mt-2 text-sm text-slate-500">{error}</p>
          <Link to="/" className="mt-4 inline-block text-sm text-blue-600 hover:underline">Back to Project Tower</Link>
        </div>
      </div>
    )
  }

  const headline = data.displayName || data.hero?.headline || data.public_name || 'Locker Room'
  const subheadline = data.hero?.subheadline || data.blurb || ''
  const about = data.about || data.bio || ''
  const quote = data.quote
  const goals = Array.isArray(data.goals) ? data.goals : []
  const wishlist = Array.isArray(data.wishlist) ? data.wishlist : []
  const topMoments = data.topMoments?.items || []
  const featuredAthletes = Array.isArray(data.featured_athletes) ? data.featured_athletes : []
  const rosterCount = Number.isFinite(data.roster_count) ? data.roster_count! : 0
  const showShareLink = data.visibility?.showShareLink !== false
  const donateUrl = data.donation_share_code
    ? `/donate?dest=locker_room&share=${encodeURIComponent(data.donation_share_code)}`
    : '/donate?dest=locker_room'

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.bg}`}>
      {/* Hero Header */}
      <header className={`bg-gradient-to-r ${theme.headerBg} relative overflow-hidden`}>
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.3\' fill-rule=\'evenodd\'%3E%3Cpath d=\'M20 0L0 20h8l12-12 12 12h8z\'/%3E%3C/g%3E%3C/svg%3E")',
          }}
        />
        <div className="relative mx-auto max-w-4xl px-6 py-12 text-center text-white">
          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-white/70">Locker Room</p>
          <h1 className="text-3xl font-extrabold md:text-4xl">{headline}</h1>
          {subheadline && <p className="mt-2 text-white/80">{subheadline}</p>}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-medium backdrop-blur">
              {rosterCount} athlete{rosterCount === 1 ? '' : 's'}
            </span>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-4 py-8 space-y-6 -mt-4">
        {/* Quote */}
        {quote?.text && (
          <section className={`${theme.cardBg} rounded-2xl p-6 shadow-lg text-center`}>
            <blockquote>
              <p
                className={`text-xl italic leading-relaxed ${theme.text} md:text-2xl`}
                style={{ fontFamily: "'Georgia', serif" }}
              >
                &ldquo;{quote.text}&rdquo;
              </p>
              {quote.attribution && (
                <footer className={`mt-3 text-sm ${theme.mutedText}`}>&mdash; {quote.attribution}</footer>
              )}
            </blockquote>
          </section>
        )}

        {/* About */}
        {about && (
          <section className={`${theme.cardBg} rounded-2xl p-6 shadow-md`}>
            <h2 className={`text-lg font-bold ${theme.text} mb-3`}>About This Locker Room</h2>
            <p className={`leading-relaxed ${theme.isBold ? 'text-gray-300' : theme.mutedText}`}>{about}</p>
          </section>
        )}

        {/* Goals + Wishlist side by side */}
        {(goals.length > 0 || wishlist.length > 0) && (
          <div className="grid gap-6 md:grid-cols-2">
            {goals.length > 0 && (
              <section className={`${theme.cardBg} rounded-2xl p-6 shadow-md`}>
                <h2 className={`text-lg font-bold ${theme.text} mb-4`}>Season Goals</h2>
                <div className="space-y-3">
                  {goals.map((goal, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span
                        className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white shadow-sm"
                        style={{ backgroundColor: accent }}
                      >
                        {i + 1}
                      </span>
                      <span className={theme.isBold ? 'text-gray-300' : theme.text}>{goal}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {wishlist.length > 0 && (
              <section className={`${theme.cardBg} rounded-2xl p-6 shadow-md`}>
                <h2 className={`text-lg font-bold ${theme.text} mb-4`}>Equipment Wishlist</h2>
                <div className="space-y-2">
                  {wishlist.map((w, i) => (
                    <div key={i} className={`flex items-center gap-3 rounded-xl p-3 ${theme.isBold ? 'bg-white/5' : 'bg-slate-50'}`}>
                      <span
                        className="flex h-8 w-8 items-center justify-center rounded-full text-white"
                        style={{ backgroundColor: accent }}
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate ${theme.isBold ? 'text-gray-200' : theme.text}`}>{w.label}</p>
                        {w.url && (
                          <a href={w.url} target="_blank" rel="noopener noreferrer" className="text-xs hover:underline" style={{ color: accent }}>
                            View item &rarr;
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Top Moments */}
        {topMoments.length > 0 && (
          <section className={`${theme.cardBg} rounded-2xl p-6 shadow-md`}>
            <h2 className={`text-lg font-bold ${theme.text} mb-4`}>Top Moments</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {topMoments
                .sort((a, b) => (a.position || 0) - (b.position || 0))
                .map((moment, idx) => (
                  <div
                    key={moment.id || idx}
                    className={`rounded-xl overflow-hidden shadow-sm border-l-4 ${theme.isBold ? 'bg-white/5' : 'bg-slate-50'}`}
                    style={{ borderLeftColor: accent }}
                  >
                    <div className="p-4">
                      <h3 className={`font-bold ${theme.text}`}>{moment.title}</h3>
                      {moment.date && <p className={`mt-1 text-xs ${theme.mutedText}`}>{moment.date}</p>}
                      {moment.summary && (
                        <p className={`mt-2 text-sm ${theme.isBold ? 'text-gray-300' : theme.mutedText}`}>{moment.summary}</p>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </section>
        )}

        {/* Roster Highlights */}
        {featuredAthletes.length > 0 && (
          <section className={`${theme.cardBg} rounded-2xl p-6 shadow-md`}>
            <h2 className={`text-lg font-bold ${theme.text} mb-4`}>Roster Highlights</h2>
            <div className="flex flex-wrap gap-2">
              {featuredAthletes.map((athlete, idx) => (
                <span
                  key={`${athlete.first_name || 'athlete'}-${idx}`}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${theme.badge}`}
                >
                  {athlete.first_name || 'Athlete'}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Support & Share */}
        <section className={`${theme.cardBg} rounded-2xl p-6 shadow-md`}>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="text-center md:text-left">
              <h2 className={`text-lg font-bold ${theme.text} mb-2`}>Support This Locker Room</h2>
              <p className={`text-sm mb-4 ${theme.mutedText}`}>
                Every donation helps provide equipment, training resources, and opportunities for athletes.
              </p>
              {showShareLink && (
                <Link
                  to={donateUrl}
                  className="inline-block rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:opacity-90"
                  style={{ backgroundColor: accent }}
                >
                  Donate to This Locker Room
                </Link>
              )}
            </div>
            <div className="text-center md:text-right">
              <h2 className={`text-lg font-bold ${theme.text} mb-2`}>Share</h2>
              <p className={`text-sm mb-4 ${theme.mutedText}`}>Help spread the word!</p>
              <SocialShareButtons
                url={shareUrl}
                title={`Check out ${headline} on Project Tower!`}
                description={about || `Support this locker room on Project Tower.`}
              />
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-6 text-center">
          <p className={`text-xs ${theme.mutedText}`}>
            Powered by{' '}
            <Link to="/" className="underline hover:opacity-80">Project Tower</Link>
          </p>
        </footer>
      </div>
    </div>
  )
}
